import { useCallback, useEffect, useRef } from 'react';

import type {
  ProposalDetail,
  ProposedAction,
} from '../../lib/proposals/core/types';

/**
 * A per-action functional updater: returns the replacement action, or null to
 * leave the action unchanged.
 */
export type ActionUpdater = (action: ProposedAction) => ProposedAction | null;

/**
 * Returns a `handleActionUpdate(updater)` callback that applies a per-action
 * functional updater to the proposal and forwards the merged result to
 * `onUpdate`.
 *
 * Why a ref instead of reading `proposal` directly: a merged create card saves
 * the primary record and each swapped association in separate awaited calls,
 * each invoking the returned callback. Building every update off the `proposal`
 * argument (captured when the callback was created) means the second call starts
 * from the pre-edit snapshot and reverts the first. Tracking the latest merged
 * proposal in a ref — advanced synchronously on every call, and resynced from
 * `proposal` whenever the parent's state round-trips back down — makes each call
 * accumulate on the previous one instead of clobbering it.
 */
export function useAccumulatingActionUpdate(
  proposal: ProposalDetail,
  onUpdate: (proposal: ProposalDetail) => void
): (updater: ActionUpdater) => void {
  const latestRef = useRef(proposal);
  useEffect(() => {
    latestRef.current = proposal;
  }, [proposal]);

  return useCallback(
    (updater: ActionUpdater) => {
      const base = latestRef.current;

      // Apply the updater to each action, keeping those that return null unchanged.
      const updatedActions = base.actions.map(a => {
        const result = updater(a);
        return result !== null ? result : a;
      });

      const nextProposal = { ...base, actions: updatedActions };
      // Advance the ref immediately so a subsequent call in the same save builds
      // on this result rather than the stale base.
      latestRef.current = nextProposal;

      onUpdate(nextProposal);
    },
    [onUpdate]
  );
}
